import {Trans} from '@lingui/macro';
import React, {PropsWithChildren} from 'react';
import styled from 'styled-components';

import {TextBox} from '../../../../libs/shared/ui/src/components/boxedInputs/textBox';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {flexCenter} from '../../../../libs/shared/ui/src/styles/mixins';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {BoxedIcon} from '../../../components/common/boxedIcon';
import {AtomsList} from '../../rules/ruleEditor/expressions/atomsList';
import {getExpressionTypeIcon} from './expressionIcons';
import {getLabelForInferredType} from './expressionLabels';
import {Expression} from './expressionModel';
import {ExpressionDependencies, ExpressionStateActions} from './expressionState';

/*
 * Props.
 */

interface ExpressionsSidePanelAtomsListProps<A extends ExpressionStateActions> {
  isReadonly: boolean;
  expression: Expression;
  expressionDependencies: ExpressionDependencies;
  actions: A;
}

/*
 * Style.
 */

const StyledExpressionsSidePanelHeaderSection = styled.section`
  margin: -16px -16px 16px;
  padding: 16px;

  border-bottom: 1px solid ${(p) => p.theme.greys.shade30};
`;

const StyledExpressionsSidePanelHeaderTitleDiv = styled.div`
  ${flexCenter};

  > :first-child {
    margin-right: 12px;
    align-self: start;
    padding-top: 10px;
  }
`;

const StyledExpressionsSidePanelNameDiv = styled.div`
  width: 100%;
`;

const StyledExpressionsSidePanelTextBox = styled(TextBox)`
  font-size: ${fontSizes.veryLarge};
  font-weight: ${fontWeights.bold};
`;

const StyledExpressionsSidePanelUnderlineDiv = styled.div`
  color: ${(p) => p.theme.greys.shade70};
  margin-top: -8px;
`;

const StyledExpressionsSidePanelTypeDiv = styled.div`
  margin-top: 14px;
  font-weight: ${fontWeights.semibold};
`;

/*
 * Component.
 */

export const ExpressionsSidePanelAtomsList = <A extends ExpressionStateActions>(
  props: PropsWithChildren<ExpressionsSidePanelAtomsListProps<A>>,
) => {
  const theme = useUserTheme();
  const {isReadonly, expression, actions} = props;

  return (
    <>
      <StyledExpressionsSidePanelHeaderSection>
        <StyledExpressionsSidePanelHeaderTitleDiv>
          <div>
            <BoxedIcon
              backgroundColor={theme.palette.teal.shade20}
              name={getExpressionTypeIcon(expression.inferredType).regular.filled}
              color={theme.palette.teal.shade50}
              size={VisualSizesEnum.LARGE}
            />
          </div>
          <StyledExpressionsSidePanelNameDiv>
            <StyledExpressionsSidePanelTextBox
              shouldAutoFocus
              size={VisualSizesEnum.LARGE}
              value={expression.name}
              onChange={(value) => actions.onUpdateExpressionName(expression.id, value)}
              placeholder={expression.defaultName}
              isDisabled={isReadonly}
            />
            {!expression.name && (
              <StyledExpressionsSidePanelUnderlineDiv>
                <Trans id="editor-expression-side-panel-expression-name-suggestion">
                  Suggested based on the steps you are adding.
                </Trans>
              </StyledExpressionsSidePanelUnderlineDiv>
            )}
          </StyledExpressionsSidePanelNameDiv>
        </StyledExpressionsSidePanelHeaderTitleDiv>

        <StyledExpressionsSidePanelTypeDiv>
          <Trans id="editor-expression-side-panel-field-type">Type: </Trans>{' '}
          {getLabelForInferredType(expression.inferredType)}
        </StyledExpressionsSidePanelTypeDiv>
      </StyledExpressionsSidePanelHeaderSection>
      <AtomsList
        atoms={expression.atoms}
        expressionDependencies={props.expressionDependencies}
        isReadOnly={isReadonly}
        onAddAtom={(newAtom, fnId) => actions.onAddAtom(expression.id, newAtom, fnId)}
        onUpdateAtom={(updatedAtom) =>
          actions.onUpdateAtom(expression.id, updatedAtom.clientId, (atom) => ({
            ...updatedAtom,
            clientId: atom.clientId,
          }))
        }
        onDeleteAtom={(atom) => actions.onDeleteAtom(expression.id, atom.clientId)}
      />
    </>
  );
};
